import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor, PrimitiveType} from 'react-intl';
import type ChartsDataContainer from '../NetatmoCommon/ChartsDataContainer';
import {convertValue, getPrecision, getRainUnitLabel} from '../Units';
import type {Units} from '../Units';

/**
 * The annual water balance's carry-over: the closing balance (rain minus
 * evapotranspiration) of the year before the selected one, which the report's
 * cumulative curve can start from instead of 0 on 1 January.
 *
 * `metrics` is `[rain, 'ET']` at the monthly scale, keyed by the station's
 * local month starts. The history's range end is INCLUSIVE, so the sum stops
 * the second before `yearStart`: ending at `yearStart` itself would add the
 * selected year's January to the year before (the bug both reports shipped
 * with). A month without evapotranspiration counts its rain alone; a month
 * without rain is not in the history at all (the store walks the first
 * metric's months), so it is not counted.
 */
export const previousYearWaterBalance = (
  history: Pick<ChartsDataContainer, 'getChartData'>,
  stationId: number | string,
  metrics: string[],
  previousYearStart: number,
  yearStart: number,
): number => {
  const rows = history.getChartData(
    stationId.toString(),
    metrics,
    'M',
    previousYearStart,
    yearStart - 1,
  ) as Record<string, (number | null)[]>;
  return Object.values(rows).reduce(
    (balance, [rain, evapotranspiration]) =>
      balance + (rain ?? 0) - (evapotranspiration ?? 0),
    0,
  );
};

type NumberFormatter = {
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
};

/**
 * A water balance as the report states it: signed ("+12.3 mm", "-0.118 in"),
 * at the rain unit's own precision — one decimal would read every inch figure
 * to the tenth — and in the reader's number format: `toFixed` wrote a "."
 * decimal in every locale, so French read "-150.3 mm" for "-150,3 mm" (#318).
 *
 * Always signed, and a balance that rounds to zero is "+0.0", never Intl's
 * negative zero "-0.0".
 */
export const formatWaterBalance = (
  balance: number,
  units: Units,
  intl: NumberFormatter,
): string => {
  const precision = getPrecision('RR_1H', units) ?? 1;
  const converted = convertValue(balance, 'Rain', units, precision);
  // `-0 === 0`: this turns the negative zero of a rounded deficit into 0.
  const value = converted === 0 ? 0 : converted;
  const number = intl.formatNumber(value, {
    // The locale's own plus and minus (sv writes U+2212, ar adds a bidi mark).
    signDisplay: 'always',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
  return `${number} ${getRainUnitLabel(units)}`;
};

/**
 * The carry-over switch's label, which is also its accessible name: "Carry
 * over 2025 (+12.3 mm)". `balance` is the amount the switch WOULD carry over,
 * whatever its state — the label read "+0.0" while the switch was off, so the
 * amount only appeared once applied (#318). The reports apply it to the chart
 * only while on.
 */
export const formatWaterBalanceCarryOver = (
  intl: NumberFormatter & {
    formatMessage: (
      descriptor: MessageDescriptor,
      values?: Record<string, PrimitiveType>,
    ) => string;
  },
  previousYear: number,
  balance: number,
  units: Units,
): string =>
  intl.formatMessage(messages.reports.annualWaterBalanceCarryOver, {
    year: previousYear,
    value: formatWaterBalance(balance, units, intl),
  });
